import React, { useState, useEffect, useMemo } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { TrendingDown, TrendingUp, RotateCcw, Bot, User, SlidersHorizontal } from 'lucide-react';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', green: '#1D9E75', red: '#E24B4A', amber: '#E8A020', slate: '#5C6B7A' };

const ACTION_META = {
  tighten: { icon: TrendingDown, color: C.red, label: 'Tightened', desc: 'STP band narrowed — fewer risks auto-issued, pricing discipline up' },
  loosen: { icon: TrendingUp, color: C.green, label: 'Loosened', desc: 'STP band widened — appetite opened on a profitable class' },
  revert: { icon: RotateCcw, color: C.amber, label: 'Reverted', desc: 'Previous calibration undone' },
};

function ImpactChart({ cls, events }) {
  if (!cls?.lr_series?.length) return null;
  const series = cls.lr_series;
  const eventDays = new Set(events.filter(e => e.action === 'tighten').map(e => String(e.timestamp).slice(0, 10)));
  const min = Math.min(...series.map(s => s.lr)) - 2;
  const max = Math.max(...series.map(s => s.lr)) + 2;
  const W = 720, H = 110, PAD = 6;
  const x = i => PAD + (i / (series.length - 1)) * (W - 2 * PAD);
  const y = v => H - PAD - ((v - min) / (max - min)) * (H - 2 * PAD);
  const path = series.map((s, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(s.lr).toFixed(1)}`).join(' ');
  const first = series[0].lr, last = series[series.length - 1].lr;
  const delta = Math.round((last - first) * 10) / 10;
  return (
    <div className="mb-5 p-3 rounded-md" style={{ background: '#FAFBFC', border: '1px solid rgba(136,150,168,0.12)' }} data-testid="calibration-impact-chart">
      <div className="flex flex-wrap items-center gap-3 mb-2">
        <span className="text-[11px] font-semibold" style={{ color: C.navy }}>Loss ratio — last {series.length} days</span>
        <span className="text-[11px] font-mono font-bold" style={{ color: delta <= 0 ? C.green : C.red }} data-testid="impact-chart-delta">
          {first}% → {last}% ({delta <= 0 ? '' : '+'}{delta} pts)
        </span>
        <span className="text-[10px]" style={{ color: C.slate }}>red markers = ARIA tighten events</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ maxHeight: 120 }}>
        <line x1={PAD} x2={W - PAD} y1={y(70)} y2={y(70)} stroke={C.red} strokeWidth="1" strokeDasharray="4 3" opacity="0.5" />
        <text x={W - PAD - 2} y={y(70) - 3} fontSize="8" fill={C.red} textAnchor="end">70% ceiling</text>
        <line x1={PAD} x2={W - PAD} y1={y(65)} y2={y(65)} stroke={C.amber} strokeWidth="1" strokeDasharray="4 3" opacity="0.5" />
        <text x={W - PAD - 2} y={y(65) - 3} fontSize="8" fill={C.amber} textAnchor="end">65% warn</text>
        <path d={path} fill="none" stroke={C.blue} strokeWidth="1.8" />
        {series.map((s, i) => eventDays.has(s.date) ? (
          <g key={i}>
            <circle cx={x(i)} cy={y(s.lr)} r="4" fill={C.red} />
            <line x1={x(i)} x2={x(i)} y1={y(s.lr) + 4} y2={H - PAD} stroke={C.red} strokeWidth="1" opacity="0.35" />
          </g>
        ) : null)}
      </svg>
    </div>
  );
}

export default function CalibrationTimelinePage() {
  const api = useApi();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [classFilter, setClassFilter] = useState('');

  useEffect(() => {
    api.get('/rates/calibration-timeline?days=90').then(r => setData(r.data))
      .catch(err => console.error('API error:', err?.message)).finally(() => setLoading(false));
  }, [api]);

  const entries = useMemo(() => {
    if (!data) return [];
    return classFilter ? data.entries.filter(e => e.class_id === classFilter) : data.entries;
  }, [data, classFilter]);

  if (loading) return <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-16 rounded-md" />)}</div>;
  if (!data) return null;
  const s = data.summary;

  return (
    <div className="space-y-4" data-testid="calibration-timeline-page">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Calibration Events (90d)', value: s.total_events, color: C.blue },
          { label: 'Tightens', value: s.tightens, color: C.red },
          { label: 'Loosens', value: s.loosens, color: C.green },
          { label: 'Executed by ARIA', value: `${s.automated_pct}%`, color: C.navy },
        ].map(k => (
          <Card key={k.label} className="p-3 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.15)' }}>
            <p className="text-[10px] uppercase tracking-wider" style={{ color: C.slate }}>{k.label}</p>
            <p className="text-xl font-bold font-mono mt-0.5" style={{ color: k.color }}>{k.value}</p>
          </Card>
        ))}
      </div>

      <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.15)' }} data-testid="calibration-class-chips">
        <div className="flex items-center gap-2 mb-3">
          <SlidersHorizontal className="w-3.5 h-3.5" style={{ color: C.blue }} />
          <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Current Position by Class</h3>
          <span className="text-[10px]" style={{ color: C.slate }}>rate config v{data.current_version} · negative = tightened (stricter STP), positive = loosened</span>
          {classFilter && (
            <button onClick={() => setClassFilter('')} className="ml-auto text-[11px] underline" style={{ color: C.blue }} data-testid="calibration-clear-filter">
              Clear filter
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {data.classes.map(c => {
            const adj = c.current_adjustment;
            const color = adj < 0 ? C.red : adj > 0 ? C.green : C.slate;
            const active = classFilter === c.class_id;
            return (
              <button key={c.class_id} onClick={() => setClassFilter(active ? '' : c.class_id)}
                className="px-2.5 py-1.5 rounded-sm text-[11px] font-medium transition-colors"
                style={{ border: `1px solid ${active ? C.blue : 'rgba(136,150,168,0.25)'}`, background: active ? '#EFF5FC' : '#fff', color: C.navy }}
                data-testid={`calibration-chip-${c.class_id}`}>
                {c.class_name} <strong className="font-mono" style={{ color }}>{adj > 0 ? `+${adj}` : adj}</strong>
                <span className="ml-1.5 text-[10px]" style={{ color: C.slate }}>{c.tightens}↓ {c.loosens}↑</span>
              </button>
            );
          })}
        </div>
      </Card>

      <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.15)' }}>
        <h3 className="text-xs font-semibold uppercase tracking-wider mb-4" style={{ color: C.navy }}>
          Pricing Decision Trace {classFilter ? `— ${entries[0]?.class_name || classFilter}` : ''}
        </h3>
        {classFilter && <ImpactChart cls={data.classes.find(c => c.class_id === classFilter)} events={entries} />}
        <div className="relative pl-6" data-testid="calibration-timeline-list">
          <div className="absolute left-[9px] top-1 bottom-1 w-px" style={{ background: 'rgba(136,150,168,0.25)' }} />
          {entries.length === 0 && <p className="text-xs" style={{ color: C.slate }}>No calibration events in the last 90 days.</p>}
          {entries.map((e, i) => {
            const meta = ACTION_META[e.action] || ACTION_META.revert;
            const Icon = meta.icon;
            return (
              <div key={i} className="relative pb-4" data-testid={`calibration-event-${i}`}>
                <div className="absolute -left-6 top-0.5 w-[19px] h-[19px] rounded-full flex items-center justify-center"
                  style={{ background: '#fff', border: `2px solid ${meta.color}` }}>
                  <Icon className="w-2.5 h-2.5" style={{ color: meta.color }} />
                </div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="text-xs font-semibold" style={{ color: C.navy }}>{e.class_name}</span>
                  <span className="text-[11px] font-bold uppercase" style={{ color: meta.color }}>{meta.label}</span>
                  <span className="text-[11px] font-mono" style={{ color: C.slate }}>
                    adjustment <strong style={{ color: C.navy }}>{e.before > 0 ? `+${e.before}` : e.before} → {e.after > 0 ? `+${e.after}` : e.after}</strong>
                  </span>
                  <span className="flex items-center gap-1 text-[11px]" style={{ color: e.automated ? C.blue : C.slate }}>
                    {e.automated ? <Bot className="w-3 h-3" /> : <User className="w-3 h-3" />}{e.actor}
                  </span>
                  {e.impact && e.action === 'tighten' && (
                    <span className="px-1.5 py-0.5 rounded-sm text-[10px] font-mono font-bold" data-testid={`calibration-impact-${i}`}
                      style={{ background: e.impact.delta_pts <= 0 ? '#E7F6EF' : '#FDF1F1', color: e.impact.delta_pts <= 0 ? C.green : C.red }}>
                      LR {e.impact.lr_at_decision}% → {e.impact.lr_now}% ({e.impact.delta_pts <= 0 ? '' : '+'}{e.impact.delta_pts} pts since)
                    </span>
                  )}
                  <span className="text-[10px] ml-auto font-mono" style={{ color: C.slate }}>
                    v{e.version} · {new Date(e.timestamp).toLocaleString()}
                  </span>
                </div>
                <p className="text-[11px] mt-0.5" style={{ color: C.slate }}>{meta.desc}</p>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
